import { Link, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

interface SidebarProps {
  /** サイドバーの開閉状態（モバイル） */
  isOpen: boolean;
  /** 閉じるハンドラ */
  onClose: () => void;
}

/** @graph-connects none */
interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
}

/**
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business サイドバーナビゲーション。スコアリング・履歴・マスタ管理へのリンクをエメラルドアクセントで表示
 * @graph-connects none
 */
export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();

  const navItems: NavItem[] = [
    {
      to: '/',
      label: t('nav.scoring'),
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      ),
    },
    {
      to: '/history',
      label: t('nav.history'),
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      to: '/master',
      label: t('nav.master'),
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
          />
        </svg>
      ),
    },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* モバイルオーバーレイ */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[calc(var(--z-sticky)+1)] lg:hidden"
          onClick={onClose}
        />
      )}

      {/* サイドバー */}
      <aside
        className={`
          fixed lg:sticky top-14 left-0 z-[calc(var(--z-sticky)+2)]
          w-60 h-[calc(100vh-3.5rem)]
          bg-[var(--color-bg-surface)] border-r border-[var(--color-border)]
          transition-transform duration-[var(--transition-slow)]
          lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <nav className="p-3 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={onClose}
              className={`
                flex items-center gap-3 px-3 py-2.5 rounded-lg
                text-sm font-medium transition-all duration-[var(--transition-fast)]
                ${
                  isActive(item.to)
                    ? 'bg-[var(--color-accent-muted)] text-[var(--color-accent-primary)] border border-[var(--color-accent-primary)]/20'
                    : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)]'
                }
              `}
            >
              {item.icon}
              {item.label}
            </Link>
          ))}
        </nav>
      </aside>
    </>
  );
}
